import React, { useState, useEffect } from "react";
import * as R from "ramda";
import styled from "styled-components";
import Dropdown from "react-bootstrap/Dropdown";
import Button from "react-bootstrap/Button";
import { ChevronDown } from "react-bootstrap-icons";
import helpIcon from "../../images/Question_Mark_Icon.svg";
import { DebounceInput } from "react-debounce-input";
import { DEBOUNCE_INPUT_PROPS } from "../../const";

import { useTranslation } from "react-i18next";
import { toClosestFraction, toDecimal } from "../../utils";

const SectionTitleH2 = styled.h2`
  font-size: 2em;
  color: #20358c;
  margin-top: 0.5em;
  text-transform: uppercase;
`;

const Label = styled("label")`
  position: absolute;
  right: 0px;
  left: auto;
  color: ${(props) => (props.ShowPlaceholder < 1 ? "#212529" : "#00000000")};

  @media (max-width: 576px) and (orientation: Portrait) {
    color: ${(props) => (props.ShowPlaceholder < 1 ? "#212529" : "#00000000")};
  }
`;

const LabelPap = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #00197b;
  text-transform: uppercase;
  margin-right: 5px;
  margin-left: 5px;
`;

const LabelF2Span = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #00197b;
  text-transform: uppercase;
  margin-right: 5px;
  margin-left: 5px;
  width: 30%;
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 320px;
  border: 1px solid #d4d4d5;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #00197b;
    box-shadow: 0 0 0 3px rgba(0, 25, 123, 0.12);
  }
`;

const singularButtonStyle = {
  backgroundColor: "#00197b",
  color: "#ffffff",
  width: "40px",
  minWidth: "40px",
  height: "auto",
  alignSelf: "stretch",
  border: "none",
  borderRadius: 0,
  padding: 0,
  flex: "0 0 40px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const selectStyle = {
  display: "flex",
  flex: "1 1 auto",
  minWidth: 0,
  height: "40px",
  paddingLeft: 12,
  paddingRight: 12,
  background: "#ffffff",
  border: "none",
  alignItems: "center",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  color: "#00197b",
};

export const SectionTitle = ({ children }) => (
  <div class="col-12">
    <SectionTitleH2>{children}</SectionTitleH2>
  </div>
);

const formControl = {
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  alignItems: "center",
  width: "100%",
};
const leftStyles = {
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  left: "12.5%",
};

const TextComponent = (props) => {
  const { classname, name, value, label, onChange, initialCurrentSpecsheet } =
    props;
  const [showPlaceholderName, setShowPlaceholderName] = useState(
    name?.length === 0 ? false : true
  );

  const handleKeyDown = (e) => {
    const value = e.target.value;
    const fieldName = e.target.name;
    const setShowPlaceholder = {
      name: setShowPlaceholderName,
    };
  };

  const handleValues = (e) => {
    const value = e.target.value;
    const fieldName = e.nativeEvent.target.name;
    const setShowPlaceholder = {
      name: setShowPlaceholderName,
    };

    if (onChange) {
      onChange(fieldName, value);
    }
  };

  return (
    <div className="">
      <DebounceInput
        className="form-control specsheet-name"
        id="specsheetName"
        value={value}
        name={name}
        style={formControl}
        onKeyDown={handleKeyDown}
        {...DEBOUNCE_INPUT_PROPS}
        {...props}
        onChange={handleValues}
      />
      <Label
        ShowPlaceholder={showPlaceholderName}
        htmlFor="specsheetName"
        style={leftStyles}
      >
        {label}
      </Label>
    </div>
  );
};

export const Text = styled(TextComponent)``;

const SelectorComponent = (props) => {
  const { t } = useTranslation();

  const {
    classname,
    values,
    name,
    source,
    labels,
    openDropdown,
    onDropdownVisibleChange,
    disabled,
    noTranslate,
    onChange,
    appSettings,
    ...restProps
  } = props;
  const options = source.map(({ value, text }) => ({
    value,
    label: text === "---" ? "" : noTranslate ? text : t(text),
  }));

  const valueLabels =
    labels ||
    source.reduce(
      (acc, { value, text }) => ({
        ...acc,
        [value]: text === "---" ? "" : noTranslate ? text : t(text),
      }),
      {}
    );

  const firstActiveValue =
    name.indexOf("f1_span") >= 0
      ? options.length > 10
        ? options[~~(options.length / 2)].value
        : ""
      : name.indexOf("f2_span") >= 0
      ? values[name.replace("f2", "f1")]
      : "";

  const selectProps =
    openDropdown !== "" && name === openDropdown && !disabled
      ? {
          open: name === openDropdown,
          onDropdownVisibleChange,
          ...restProps,
        }
      : { disabled, ...restProps };

  const handleSelectChange = (event) => {
    const value = event.currentTarget.attributes.value.value;
    onChange && onChange(name, value);
  };

  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <SelectContainer>
      <div style={selectStyle}>{children}</div>
      <Button
        variant="primary"
        type="submit"
        style={singularButtonStyle}
        onClick={onClick}
      >
        <ChevronDown size={16} color="#ffffff" />
      </Button>
    </SelectContainer>
  ));

  // Dropdown needs access to the DOM of the Menu to measure it
  const CustomMenu = React.forwardRef(
    (
      { children, style, className, show, "aria-labelledby": labeledBy },
      ref
    ) => {
      const [value, setValue] = useState("");
      const ulRef = React.createRef();

      useEffect(() => {
        if (show) {
          if (values && values[name] && values[name] !== "") {
            const activeItem = ulRef.current.querySelector(
              ".dropdown-item.active"
            );
            activeItem && activeItem.scrollIntoView();
          } else {
            if (firstActiveValue !== "") {
              const activeItem = ulRef.current.querySelector(
                `.dropdown-item[value="${firstActiveValue}"]`
              );
              activeItem && activeItem.scrollIntoView();
            }
          }
        }
      });

      return (
        <div
          ref={ref}
          style={style}
          className={`${className} dropdown-custom-components`}
          aria-labelledby={labeledBy}
        >
          <ul
            className="list-unstyled"
            style={{ maxHeight: 300, overflow: "scroll", minWidth: "200px" }}
            ref={ulRef}
          >
            {React.Children.toArray(children).filter(
              (child) =>
                !value || child.props.children.toLowerCase().startsWith(value)
            )}
          </ul>
        </div>
      );
    }
  );

  const findPoint = (cx, cy, rad, cornerGrad) => {
    var cornerRad = (cornerGrad * Math.PI) / 180;
    var nx = Math.cos(cornerRad) * rad + cx;
    var ny = Math.sin(cornerRad) * rad + cy;
    return { x2: nx, y2: ny };
  };

  const degreeMultiplier = () => {
    switch (appSettings.systemDefault.pitch_measurement) {
      case "16+":
        return 1.875;
      case "32":
        return 1.875;
      case "32+":
        return 0.9375;
      case "64":
        return 0.9375;
      default:
        return 3.75;
    }
  };

  console.log(
    "value in toggle ->",
    `${
      values
        ? values[name] && values[name].indexOf(": ") === -1
          ? valueLabels[values[name]] ||
            valueLabels[
              toClosestFraction(
                values[name],
                appSettings.systemDefault.span_measurement
              )
            ] ||
            ""
          : values[name] && values[name].indexOf(": ") >= 0
          ? valueLabels[values[name]] || ""
          : ""
        : ""
    }`
  );

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle}>
          {`${
            values
              ? values[name] && values[name].indexOf(": ") === -1
                ? valueLabels[values[name]] ||
                  valueLabels[
                    toClosestFraction(
                      values[name],
                      appSettings.systemDefault.span_measurement
                    )
                  ] ||
                  ""
                : values[name] && values[name].indexOf(": ") >= 0
                ? valueLabels[values[name]] || ""
                : ""
              : ""
          }`}
        </Dropdown.Toggle>
        <Dropdown.Menu as={CustomMenu}>
          {options.map(({ value, label }, idx) => {
            return (
              <Dropdown.Item
                eventKey={value}
                value={value}
                onClick={handleSelectChange}
                active={
                  value ===
                  (values[name] ||
                    toClosestFraction(
                      values[name],
                      appSettings.systemDefault.span_measurement
                    ))
                }
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{label}</span>
                  {values["ball_grip"] !== "conventional" &&
                    R.trim(label) !== "" &&
                    toDecimal(
                      value,
                      appSettings.systemDefault.span_measurement
                    ) <= 1.5 &&
                    (name === "f1_rev" ||
                      name === "f1_for" ||
                      name === "f2_rev" ||
                      name === "f2_for") && (
                      <div style={{ width: "35px" }}>
                        <svg
                          viewBox="0 0 100 100"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <line
                            x1="50"
                            y1="25"
                            x2="100"
                            y2="25"
                            stroke="black"
                            strokeWidth={3}
                          />
                          <line
                            x1="50"
                            y1="25"
                            {...findPoint(
                              50,
                              25,
                              50,
                              90 + (idx - 1) * degreeMultiplier()
                            )}
                            stroke="black"
                            strokeWidth={3}
                          />
                        </svg>
                      </div>
                    )}
                </div>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

const PapComponent = (props) => {
  const { t } = useTranslation();

  const {
    classname,
    values,
    name,
    source,
    openDropdown,
    onDropdownVisibleChange,
    disabled,
    noTranslate,
    onChange,
    label,
    ...restProps
  } = props;
  const options = source.map(({ value, text }) => ({
    value,
    label: text === "---" ? "" : noTranslate ? text : t(text),
  }));

  const firstActiveValue =
    name.indexOf("f1_span") >= 0
      ? options.length > 10
        ? options[~~(options.length / 2)].value
        : ""
      : name.indexOf("f2_span") >= 0
      ? values[name.replace("f2", "f1")]
      : "";

  const selectProps =
    openDropdown !== "" && name === openDropdown && !disabled
      ? {
          open: name === openDropdown,
          onDropdownVisibleChange,
          ...restProps,
        }
      : { disabled, ...restProps };

  const handleSelectChange = (event) => {
    const value = event.target.attributes.value.value;
    onChange && onChange(name, value);
  };

  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <SelectContainer
      style={{ marginBottom: 10, maxWidth: 180, margin: "0 auto 10px" }}
    >
      <LabelPap>{label}</LabelPap>
      <div
        style={{
          ...selectStyle,
          width: 90,
          justifyContent: "flex-end",
          paddingLeft: 4,
          paddingRight: 12,
        }}
      >
        {children}
      </div>
      <Button
        variant="primary"
        type="submit"
        style={singularButtonStyle}
        onClick={onClick}
      >
        <ChevronDown size={16} color="#ffffff" />
      </Button>
    </SelectContainer>
  ));

  // Dropdown needs access to the DOM of the Menu to measure it
  const CustomMenu = React.forwardRef(
    (
      { children, style, className, show, "aria-labelledby": labeledBy },
      ref
    ) => {
      const [value, setValue] = useState("");
      const ulRef = React.createRef();

      useEffect(() => {
        if (show && name === "pap_h" && ulRef.current) {
          // Find the item with value "4 1/4"
          const targetItem = ulRef.current.querySelector(
            `.dropdown-item[value="4 1/4"]`
          );
          if (targetItem) {
            targetItem.scrollIntoView({ behavior: "auto", block: "center" });
          }
        }
      }, [show, name]); // Trigger when the dropdown is opened

      return (
        <div
          ref={ref}
          style={style}
          className={`${className} dropdown-custom-components`}
          aria-labelledby={labeledBy}
        >
          <ul
            className="list-unstyled"
            style={{ maxHeight: 300, overflow: "scroll" }}
            ref={ulRef}
          >
            {React.Children.toArray(children).filter(
              (child) =>
                !value || child.props.children.toLowerCase().startsWith(value)
            )}
          </ul>
        </div>
      );
    }
  );

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle}>
          {`${values ? values[name] || "" : ""}`}
        </Dropdown.Toggle>
        <Dropdown.Menu as={CustomMenu}>
          {options.map(({ value, label }) => {
            return (
              <Dropdown.Item
                eventKey={value}
                value={value}
                onClick={handleSelectChange}
                active={value === values[name]}
              >
                {label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

const F2SpanComponent = (props) => {
  const { t } = useTranslation();

  const {
    classname,
    values,
    name,
    source,
    sourceDifference,
    openDropdown,
    onDropdownVisibleChange,
    disabled,
    noTranslate,
    onChange,
    onChangeDifference,
    label,
    labelDifference,
    labelsDifference,
    appSettings,
    ...restProps
  } = props;
  const options = source.map(({ value, text }) => ({
    value,
    label: text === "---" ? "" : noTranslate ? text : t(text),
  }));

  const optionsDifference = sourceDifference.map(({ value, text }) => ({
    value,
    label: text === "---" ? "" : noTranslate ? text : t(text),
  }));

  const valueLabelsDifference =
    labelsDifference ||
    sourceDifference.reduce(
      (acc, { value, text }) => ({
        ...acc,
        [value]: text === "---" ? "" : noTranslate ? text : t(text),
      }),
      {}
    );

  const firstActiveValue =
    name.indexOf("f1_span") >= 0
      ? options.length > 10
        ? options[~~(options.length / 2)].value
        : ""
      : name.indexOf("f2_span") >= 0
      ? values[name.replace("f2", "f1")]
      : "";

  const selectProps =
    openDropdown !== "" && name === openDropdown && !disabled
      ? {
          open: name === openDropdown,
          onDropdownVisibleChange,
          ...restProps,
        }
      : { disabled, ...restProps };

  const handleSelectChange = (event) => {
    const value = event.target.attributes.value.value;
    onChange && onChange(name, value);
  };

  const handleSelectChangeDifference = (event) => {
    const value = event.target.attributes.value.value;
    onChangeDifference && onChangeDifference(name, value);
  };

  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <SelectContainer style={{ marginBottom: 10 }}>
      <LabelF2Span>{label}</LabelF2Span>
      <div style={{ ...selectStyle, width: "60%" }}>{children}</div>
      <Button
        variant="primary"
        type="submit"
        style={singularButtonStyle}
        onClick={onClick}
      >
        <ChevronDown size={16} color="#ffffff" />
      </Button>
    </SelectContainer>
  ));

  const CustomToggleDifference = React.forwardRef(
    ({ children, onClick }, ref) => (
      <SelectContainer style={{ marginBottom: 10 }}>
        <LabelF2Span
          style={{
            backgroundImage: `url(${helpIcon})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "100% 2px",
            backgroundSize: "12px",
          }}
        >
          <a
            href={
              "https://wiki.spectrebowling.com/books/09-tips-troubleshooting-reference/page/933-516-rule-ring-finger-span-distance-chart"
            }
            target={"_blank"}
            style={{
              color: "inherit",
            }}
          >
            {labelDifference}
          </a>
        </LabelF2Span>
        <div style={{ ...selectStyle, width: "60%" }}>{children}</div>
        <Button
          variant="primary"
          type="submit"
          style={singularButtonStyle}
          onClick={onClick}
        />
      </SelectContainer>
    )
  );

  // Dropdown needs access to the DOM of the Menu to measure it
  const CustomMenu = React.forwardRef(
    (
      { children, style, className, show, "aria-labelledby": labeledBy },
      ref
    ) => {
      const [value, setValue] = useState("");
      const ulRef = React.createRef();

      useEffect(() => {
        if (show) {
          if (values && values[name] && values[name] !== "") {
            const activeItem = ulRef.current.querySelector(
              ".dropdown-item.active"
            );
            activeItem && activeItem.scrollIntoView();
          } else {
            if (firstActiveValue !== "") {
              const activeItem = ulRef.current.querySelector(
                `.dropdown-item[value="${firstActiveValue}"]`
              );
              activeItem && activeItem.scrollIntoView();
            }
          }
        }
      });

      return (
        <div
          ref={ref}
          style={style}
          className={`${className} dropdown-custom-components`}
          aria-labelledby={labeledBy}
        >
          <ul
            className="list-unstyled"
            style={{ maxHeight: 300, overflow: "scroll" }}
            ref={ulRef}
          >
            {React.Children.toArray(children).filter(
              (child) =>
                !value || child.props.children.toLowerCase().startsWith(value)
            )}
          </ul>
        </div>
      );
    }
  );

  return (
    <>
      <Dropdown style={{ width: "100%" }}>
        <Dropdown.Toggle as={CustomToggle}>
          {`${values ? values[name] || "" : ""}`}
        </Dropdown.Toggle>
        <Dropdown.Menu as={CustomMenu}>
          {options.map(({ value, label }) => {
            return (
              <Dropdown.Item
                eventKey={value}
                value={value}
                onClick={handleSelectChange}
                active={value === values[name]}
              >
                {label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      <Dropdown style={{ width: "100%" }}>
        <Dropdown.Toggle as={CustomToggleDifference}>
          {`${
            values
              ? values["f2_span_full_difference"]
                ? valueLabelsDifference[
                    toClosestFraction(
                      values["f2_span_full_difference"],
                      appSettings.systemDefault.span_measurement
                    )
                  ] || ""
                : ""
              : ""
          }`}
        </Dropdown.Toggle>
        <Dropdown.Menu as={CustomMenu}>
          {optionsDifference.map(({ value, label }) => {
            return (
              <Dropdown.Item
                eventKey={value}
                value={value}
                onClick={handleSelectChangeDifference}
                active={value === values["f2_span_full_difference"]}
              >
                {label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export const Selector = styled(SelectorComponent)``;
export const PapSelector = styled(PapComponent)``;
export const F2SpanSelector = styled(F2SpanComponent)``;

export const FormContent = styled.div`
  padding: 1em;
  overflow-y: auto;
  height: calc(100vh - 100px);
  -webkit-overflow-scroll: touch;
  & form {
    font-size: 10px;
  }

  & .ant-row.ant-form-item {
    margin: 0;

    .ant-form-item-label label {
      font-size: 1.2em;
      color: #6c757e;
      text-transform: capitalize;
    }

    .ant-input {
      border-color: black;
    }

    .ant-select {
      min-width: 100% !important;

      .ant-select-selection {
        border-color: black;
      }

      .ant-select-arrow {
        color: rgba(0, 0, 0, 0.5);
      }
    }
  }
`;
